import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { InvoiceIcon, ReportIcon } from "@/components/icons"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command"
import {
  SherpaDialog,
  SherpaDialogContent,
  SherpaDialogTrigger,
  SherpaDialogHeader,
  SherpaDialogTitle,
} from "@/components/ui-kit/dialog"
import { useIsMobile } from "@/hooks/useIsMobile"
import { DocumentsApi, openDocument } from "@/lib/documents-api"
import type { Contract } from "@/types/contract"

function ContractsDocumentsMenuItems({
  contract,
  onClose,
}: {
  contract: Contract
  onClose: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isContractLoading, setIsContractLoading] = useState(false)
  const invoicesCount = contract.invoices?.length ?? 0

  const handleOpenContract = async () => {
    if (isContractLoading) {
      return
    }

    setIsContractLoading(true)

    try {
      const document = await DocumentsApi.getDocument("contracts", contract.id)
      openDocument(document)
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Не удалось получить договор"
      toast.error(message)
    } finally {
      setIsContractLoading(false)
    }
  }

  const handleOpenInvoices = () => {
    navigate(`/invoices/${contract.id}`)
    onClose()
  }

  return (
    <Command className="p-0 rounded-[8px]! border-none! shadow-none!">
      <CommandList className="p-0">
        <CommandGroup className="p-0">
          <CommandItem
            className="cursor-pointer sm:p-4 p-6 px-0 rounded-[0px] first:rounded-t-8 data-[selected=true]:bg-card hover:text-brand justify-between sm:justify-start"
            disabled={isContractLoading}
            onSelect={() => {
              void handleOpenContract()
            }}
          >
            <ReportIcon size={20} className="size-5 sm:order-1 order-2" />
            <span className="sm:order-2 order-1 text-[16px] leading-[22px] sm:text-[18px] sm:leading-[26px]">
              Договор
            </span>
            <CommandShortcut className="hidden!" />
          </CommandItem>
          {invoicesCount > 0 ? (
            <>
              <CommandSeparator className="bg-border" />
              <CommandItem
                className="cursor-pointer sm:p-4 p-6 px-0 rounded-[0px] last:rounded-b-8 data-[selected=true]:bg-card hover:text-brand justify-between sm:justify-start"
                onSelect={handleOpenInvoices}
              >
              <InvoiceIcon size={20} className="size-5 sm:order-1 order-2" />
              <span className="sm:order-2 order-1 text-[16px] leading-[22px] sm:text-[18px] sm:leading-[26px]">
                {t("contracts.invoicesCount", { count: invoicesCount })}
              </span>
              <CommandShortcut className="hidden!" />
            </CommandItem>
            </>
          ) : null}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

export function ContractsDocumentsMenu({ contract }: { contract: Contract }) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const prevIsMobile = useRef(isMobile)

  useEffect(() => {
    if (prevIsMobile.current !== isMobile) {
      setOpen(false)
      prevIsMobile.current = isMobile
    }
  }, [isMobile])

  const trigger = (
    <button
      type="button"
      className="cursor-pointer absolute sm:top-6 top-4 sm:right-6 right-4 flex items-center justify-center text-brand transition-colors hover:text-[#70990c]"
      aria-label="Документы"
    >
      <InvoiceIcon size={20} className="size-[20px]" />
    </button>
  )

  if (isMobile) {
    return (
      <SherpaDialog open={open} onOpenChange={setOpen}>
        <SherpaDialogTrigger asChild>{trigger}</SherpaDialogTrigger>
        <SherpaDialogContent className="max-sm:left-0 max-sm:h-auto max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:[--sherpa-dialog-height:auto]">
          <SherpaDialogHeader className="mb-2">
            <SherpaDialogTitle>
              Бокс {contract.unitItem?.code ?? contract.unit}
            </SherpaDialogTitle>
          </SherpaDialogHeader>
          <ContractsDocumentsMenuItems contract={contract} onClose={() => setOpen(false)} />
        </SherpaDialogContent>
      </SherpaDialog>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-max p-0 rounded-[8px] border-none! ring-0! shadow-none! shadow-(--menu)! mt-2"
        align="end"
      >
        <ContractsDocumentsMenuItems contract={contract} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
